import React from 'react'
import clsx from 'clsx'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'icon'
    | 'icon-left'
    | 'icon-right'
    | 'icon-outline'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  children: React.ReactNode
  className?: string
}

export function Button({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  ...props
}: ButtonProps) {
  /**
   * BASE — FULL ROUND + AllRound Gothic everywhere
   */
  const baseStyles = `
    inline-flex items-center justify-center
    font-allRoundGothic font-semibold 
    normal-case tracking-wide leading-tight
    transition-all duration-300 ease-out
    rounded-full
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kuma-verde focus-visible:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    whitespace-nowrap
  `

  /**
   * VARIANTS
   */
  const variantStyles: Record<string, string> = {
    primary: `
      bg-kuma-naranja text-white
      hover:bg-[#FF9255] active:scale-[0.97]
      shadow-md hover:shadow-lg
    `,

    outline: `
      border-2 border-kuma-naranja text-kuma-naranja 
      bg-transparent 
      hover:bg-kuma-naranja hover:text-white 
      active:scale-[0.97]
    `,

    secondary: `
      bg-kuma-tierra text-white 
      hover:bg-[#6e6347] 
      shadow-sm hover:shadow-md active:scale-[0.97]
    `,

    ghost: `
  text-kuma-tierra
  bg-transparent

  hover:bg-kuma-beige/40

  focus-visible:outline-none
  focus-visible:ring-2
  focus-visible:ring-kuma-tierra/30
  focus-visible:ring-offset-2
  focus-visible:ring-offset-transparent

  active:scale-[0.98]

  transition-colors transition-transform
  duration-200
  ease-out
`,

    icon: `
      bg-kuma-naranja text-white
      rounded-full 
      hover:bg-[#FF9255]
      shadow-sm hover:shadow-md active:scale-95
    `,

    'icon-left': `
      inline-flex items-center gap-[10px]
      bg-kuma-naranja text-white 
      hover:bg-[#FF9255]
      shadow-sm hover:shadow-md active:scale-[0.97]
    `,

    'icon-right': `
      inline-flex items-center gap-[10px] flex-row-reverse
      bg-kuma-naranja text-white
      hover:bg-[#FF9255]
      shadow-sm hover:shadow-md active:scale-[0.97]
    `,

    'icon-outline': `
      border-2 border-kuma-naranja text-kuma-naranja
      bg-transparent hover:bg-kuma-naranja hover:text-white
      shadow-sm hover:shadow-md active:scale-[0.97]
    `,
  }

  /**
   * SIZES — FULL ROUND / PILL STYLE (versión refinada)
   */
  const sizeStyles: Record<string, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-5 py-2 text-base',
    lg: 'px-6 py-2.5 text-base',
    icon: 'h-9 w-9',
  }

  const combinedClassName = clsx(
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    className
  )

  return (
    <button className={combinedClassName} {...props}>
      {children}
    </button>
  )
}
